export type EagreementsGuardState = {
  isMandatoryEnabled: boolean;
  isFlagResolved: boolean;
  areDocumentsSigned: boolean;
  outstandingDocuments: any[];
  loadingOutstandingDocuments: boolean;
  missingProfileDetailsBlockingBooking: boolean;
};

type EagreementsGuardDependencies = {
  onFeatureFlagsLoaded: (callback: () => void) => () => void;
  emitStateChanged: () => void;
  getOutstandingDocuments: (options?: unknown) => Promise<any[]>;
  sendUnsignedDocumentsEvent: (documents: any[], mandatory: boolean) => void;
  goToDocument: (url: string) => void;
  isFeatureFlagEnabled: (flag: string) => boolean;
  isFlagResolved: (flag: string) => boolean;
  getCurrentUser: () => any;
  showProfileModal: () => Promise<{
    element: { modal: () => void };
    close: Promise<unknown>;
  }>;
  logError?: (error: unknown) => void;
};

const EAGREEMENTS_FLAG = 'is-eagreements-mandatory-enabled';
const EMERGENCY_CONTACT_FLAG = 'is-booking-blocked-by-emergency-contact-enabled';

function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (Array.isArray(value) || typeof value === 'string') return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return true;
}

export function createEagreementsGuardService(dependencies: EagreementsGuardDependencies) {
  const state: EagreementsGuardState = {
    isMandatoryEnabled: false,
    isFlagResolved: false,
    areDocumentsSigned: true,
    outstandingDocuments: [],
    loadingOutstandingDocuments: false,
    missingProfileDetailsBlockingBooking: false,
  };

  let emergencyContactBookingBlocked = false;
  let unbindFeatureFlagsLoaded: (() => void) | null = null;
  let sessionId = 0;

  function evaluateFlags() {
    state.isMandatoryEnabled = dependencies.isFeatureFlagEnabled(EAGREEMENTS_FLAG);
    state.isFlagResolved = dependencies.isFlagResolved(EAGREEMENTS_FLAG);
    emergencyContactBookingBlocked =
      dependencies.isFeatureFlagEnabled(EMERGENCY_CONTACT_FLAG);
  }

  function updateMissingProfileDetailsBlockingBooking() {
    const user = dependencies.getCurrentUser();
    const userHasNoEmergencyContact = !isEmpty(user) && isEmpty(user.emergency_contact);
    const mandatoryAgreementsMissing =
      !state.areDocumentsSigned && state.isMandatoryEnabled;
    state.missingProfileDetailsBlockingBooking =
      userHasNoEmergencyContact &&
      emergencyContactBookingBlocked &&
      !mandatoryAgreementsMissing;
  }

  function refresh(options?: unknown) {
    const refreshSession = sessionId;
    state.loadingOutstandingDocuments = true;
    return dependencies
      .getOutstandingDocuments(options)
      .then(
        (response) => {
          if (refreshSession !== sessionId) return;
          state.outstandingDocuments = response;
          if (state.isMandatoryEnabled) {
            state.areDocumentsSigned = response.length === 0;
          }
          dependencies.sendUnsignedDocumentsEvent(
            state.outstandingDocuments,
            state.isMandatoryEnabled
          );
          updateMissingProfileDetailsBlockingBooking();
        },
        (error) => {
          if (refreshSession !== sessionId) return;
          if (state.isMandatoryEnabled) {
            state.areDocumentsSigned = false;
          }
          updateMissingProfileDetailsBlockingBooking();
          dependencies.logError?.(error);
        }
      )
      .finally(() => {
        if (refreshSession !== sessionId) return;
        state.loadingOutstandingDocuments = false;
        dependencies.emitStateChanged();
      });
  }

  function onFeatureFlagsLoaded() {
    evaluateFlags();
    const user = dependencies.getCurrentUser();
    if (state.isMandatoryEnabled && !isEmpty(user)) {
      void refresh();
    } else {
      updateMissingProfileDetailsBlockingBooking();
    }
  }

  function activate() {
    sessionId += 1;
    evaluateFlags();
    state.outstandingDocuments = [];
    state.loadingOutstandingDocuments = false;
    state.areDocumentsSigned = !state.isMandatoryEnabled;
    updateMissingProfileDetailsBlockingBooking();
    if (!unbindFeatureFlagsLoaded) {
      unbindFeatureFlagsLoaded = dependencies.onFeatureFlagsLoaded(onFeatureFlagsLoaded);
    }
    return state;
  }

  function deactivate() {
    sessionId += 1;
    unbindFeatureFlagsLoaded?.();
    unbindFeatureFlagsLoaded = null;
  }

  function goToDocument(documentUrl: string) {
    if (state.isMandatoryEnabled) {
      state.areDocumentsSigned = true;
      updateMissingProfileDetailsBlockingBooking();
    }
    dependencies.goToDocument(documentUrl);
  }

  function openProfile() {
    return dependencies
      .showProfileModal()
      .then((modal) => {
        modal.element.modal();
        modal.close.then();
        return modal;
      })
      .catch((error) => {
        dependencies.logError?.(error);
        return undefined;
      });
  }

  function isMissingProfileDetailsBlockingBooking() {
    updateMissingProfileDetailsBlockingBooking();
    return state.missingProfileDetailsBlockingBooking;
  }

  function getState() {
    updateMissingProfileDetailsBlockingBooking();
    return state;
  }

  function attachTo(vm: Record<string, any>) {
    activate();
    const mirrors: Record<string, keyof EagreementsGuardState> = {
      isEagreementsMandatoryEnabled: 'isMandatoryEnabled',
      isEagreementsFlagResolved: 'isFlagResolved',
      areDocumentsSigned: 'areDocumentsSigned',
      outstandingDocuments: 'outstandingDocuments',
      loadingOutstandingDocuments: 'loadingOutstandingDocuments',
    };
    Object.entries(mirrors).forEach(([vmKey, stateKey]) => {
      Object.defineProperty(vm, vmKey, {
        configurable: true,
        enumerable: true,
        get: () => state[stateKey],
        set: (value) => {
          (state as any)[stateKey] = value;
        },
      });
    });
    vm.goToOutstandingDocuments = goToDocument;
    vm.openProfileDialog = openProfile;
    vm.isMissingProfileDetailsBlockingBooking =
      isMissingProfileDetailsBlockingBooking;
    return state;
  }

  return {
    getState,
    activate,
    deactivate,
    attachTo,
    refresh,
    goToDocument,
    openProfile,
    isMissingProfileDetailsBlockingBooking,
  };
}
